import { useEffect, useMemo, useState } from 'react'
import { useStore } from '../store/store'
import { stockAdapter } from '../data/adapters'
import type { Candle, StrategyEvaluation } from '../domain/types'
import { evaluateStrategy, paramsToRecord, type Objective, type StockStrategyType } from '../lib/math'
import { fmtPctValue, fmtSigned } from '../lib/format'
import { Banner, Button, Card, NumberField, PageHead, Pill, SelectField, Stat, TextField, type Tone } from '../components/ui'
import { BarChart, Gauge, Heatmap, Histogram } from '../components/charts'

const LEVEL_TONE: Record<string, Tone> = { low: 'green', elevated: 'amber', high: 'amber', severe: 'red' }

export function StrategyEvaluation() {
  const assets = useStore((s) => s.assets)
  const saveStrategy = useStore((s) => s.saveStrategy)

  const [assetId, setAssetId] = useState(assets[0]?.id ?? '')
  const asset = assets.find((a) => a.id === assetId) ?? assets[0]
  const symbol = asset?.symbol ?? 'NOVA'

  const [type, setType] = useState<StockStrategyType>('sma-cross')
  const [objective, setObjective] = useState<Objective>('return')
  const [fee, setFee] = useState(0)
  const [slippage, setSlippage] = useState(0.0005)
  const [spread, setSpread] = useState(0.0005)
  const [source, setSource] = useState<'synthetic' | 'stored'>('synthetic')
  const [synth, setSynth] = useState<Candle[]>([])
  const [name, setName] = useState('')
  const [saved, setSaved] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    stockAdapter.fetchCandles(symbol, 500).then((c) => {
      if (alive) setSynth(c)
    })
    return () => {
      alive = false
    }
  }, [symbol])

  const candles = useMemo(() => (source === 'stored' ? asset?.candles ?? [] : synth), [source, asset, synth])
  const costs = useMemo(() => ({ feePct: fee, slippagePct: slippage, spreadPct: spread }), [fee, slippage, spread])
  const bundle = useMemo(
    () => (candles.length >= 120 ? evaluateStrategy(candles, type, costs, objective) : null),
    [candles, type, costs, objective],
  )

  const buildEvaluation = (status: StrategyEvaluation['status'], causeOfDeath?: string, autopsy?: string): Omit<StrategyEvaluation, 'id' | 'createdAt'> | null => {
    if (!bundle) return null
    return {
      name: name || `${symbol} ${type}`,
      assetSymbol: symbol,
      strategyType: type,
      params: paramsToRecord(bundle.bestParams),
      nConfigs: bundle.nConfigs,
      metrics: bundle.metrics,
      train: bundle.train,
      test: bundle.test,
      degradationPct: bundle.degradationPct,
      walkForward: bundle.walkForward.folds,
      wfEfficiency: bundle.walkForward.efficiency,
      wfConsistency: bundle.walkForward.consistency,
      sensitivity: bundle.sensitivity,
      resample: {
        observedReturnPct: bundle.bootstrap.observedReturnPct,
        p5ReturnPct: bundle.bootstrap.p5ReturnPct,
        medianReturnPct: bundle.bootstrap.medianReturnPct,
        p95ReturnPct: bundle.bootstrap.p95ReturnPct,
        pProfit: bundle.bootstrap.pProfit,
        observedMaxDdPct: bundle.permutation.observedMaxDdPct,
        p95MaxDdPct: bundle.permutation.p95MaxDdPct,
      },
      pbo: bundle.pbo.pbo,
      eraReplay: bundle.eraReplay.eras,
      costSensitivity: bundle.costSensitivity,
      overfit: bundle.overfit,
      redTeam: bundle.redTeam,
      status,
      causeOfDeath,
      autopsy,
    }
  }

  const save = () => {
    const e = buildEvaluation('candidate')
    if (e) {
      saveStrategy(e)
      setSaved('Saved as a candidate. Compare it on the Strategy Lab page.')
    }
  }
  const bury = () => {
    if (!bundle) return
    const note = window.prompt('Autopsy — why are you burying this idea?', bundle.overfit.reasons[0] ?? 'Did not survive evaluation.')
    const e = buildEvaluation('buried', `Overfit risk ${bundle.overfit.level}`, note ?? 'Buried after red-team review.')
    if (e) {
      saveStrategy(e)
      setSaved('Buried in the Strategy Graveyard. Good discipline.')
    }
  }

  if (!asset) return <div className="empty">No assets loaded.</div>

  return (
    <div className="stack">
      <PageHead title="Strategy Evaluation Lab">
        Optimize a rule, then attack it. EdgeLab tries every parameter set, splits train from test, walks forward, resamples, and aggressively warns when a strategy only looks good because of curve fitting, small samples, or unrealistic assumptions.
      </PageHead>

      <Card title="Configure">
        <div className="grid cols-4">
          <SelectField label="Asset" value={assetId} onChange={setAssetId} options={assets.map((a) => ({ value: a.id, label: a.symbol }))} />
          <SelectField label="Strategy family" value={type} onChange={setType} options={[{ value: 'sma-cross', label: 'SMA crossover' }, { value: 'rsi-reversion', label: 'RSI reversion' }, { value: 'momentum', label: 'Momentum' }]} />
          <SelectField label="Optimize for" value={objective} onChange={setObjective} options={[{ value: 'return', label: 'Total return' }, { value: 'sharpe', label: 'Sharpe-like' }, { value: 'calmar', label: 'Return / drawdown' }]} />
          <SelectField label="History" value={source} onChange={setSource} options={[{ value: 'synthetic', label: '500-bar synthetic' }, { value: 'stored', label: `Stored (${asset.candles.length} bars)` }]} />
          <NumberField label="Fee %" value={fee} onChange={setFee} step={0.0005} />
          <NumberField label="Slippage %" value={slippage} onChange={setSlippage} step={0.0005} />
          <NumberField label="Spread %" value={spread} onChange={setSpread} step={0.0005} />
          <div className="field"><label><span>Bars used</span></label><div className="input mono" style={{ paddingTop: 9 }}>{candles.length}</div></div>
        </div>
      </Card>

      {saved && <Banner tone="info">{saved}</Banner>}

      {!bundle ? (
        <div className="empty">Need at least 120 bars to evaluate. Switch history source or import more data.</div>
      ) : (
        <>
          <div className="grid cols-3">
            <Card title="Overfit risk" subtitle="Composite of sample size, IS/OOS gap, walk-forward, fragility, luck, costs & PBO">
              <Gauge value={bundle.overfit.score} />
              <div className="center"><Pill tone={LEVEL_TONE[bundle.overfit.level]}>{bundle.overfit.level.toUpperCase()} RISK</Pill></div>
            </Card>
            <div className="stack" style={{ gridColumn: 'span 2' }}>
              <Banner tone={bundle.overfit.level === 'severe' || bundle.overfit.level === 'high' ? 'danger' : bundle.overfit.level === 'elevated' ? 'warn' : 'info'}>
                <strong>Why this score:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {bundle.overfit.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </Banner>
              <div className="grid cols-4">
                <Stat label="Best params" value={Object.entries(paramsToRecord(bundle.bestParams)).map(([k, v]) => `${k}=${v}`).join(' ')} />
                <Stat label="Full return" value={fmtPctValue(bundle.metrics.totalReturnPct)} tone={bundle.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'} />
                <Stat label="Trades" value={String(bundle.metrics.trades)} tone={bundle.metrics.trades < 10 ? 'neg' : undefined} />
                <Stat label="Configs tried" value={String(bundle.nConfigs)} sub="multiple-testing risk" />
              </div>
            </div>
          </div>

          <div className="grid cols-2">
            <Card title="Train / test split" subtitle="Best config chosen on train (70%), measured on untouched test (30%)">
              <div className="kv"><span className="k">Train return</span><span className={`v ${bundle.train.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(bundle.train.totalReturnPct, 1)}%</span></div>
              <div className="kv"><span className="k">Test return</span><span className={`v ${bundle.test.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(bundle.test.totalReturnPct, 1)}%</span></div>
              <div className="kv"><span className="k">Degradation</span><span className="v">{fmtPctValue(bundle.degradationPct, 0)}</span></div>
              <div className="kv"><span className="k">Test trades</span><span className="v">{bundle.test.trades}</span></div>
              {bundle.train.totalReturnPct > 0 && bundle.test.totalReturnPct <= 0 && <Banner tone="danger">Profitable in training, flat/negative on test — the classic overfit signature.</Banner>}
            </Card>

            <Card title="Walk-forward optimization" subtitle="Each fold re-optimizes on the past, trades the next unseen window">
              {bundle.walkForward.insufficient ? (
                <div className="empty">Not enough data for walk-forward. Use the 500-bar synthetic history.</div>
              ) : (
                <>
                  <div className="grid cols-3">
                    <Stat label="OOS return" value={fmtPctValue(bundle.walkForward.oosReturnPct)} tone={bundle.walkForward.oosReturnPct >= 0 ? 'pos' : 'neg'} />
                    <Stat label="Efficiency" value={fmtPctValue(bundle.walkForward.efficiency * 100, 0)} sub="OOS / IS" tone={bundle.walkForward.efficiency < 0.5 ? 'neg' : 'pos'} />
                    <Stat label="Consistency" value={fmtPctValue(bundle.walkForward.consistency * 100, 0)} sub="folds positive" />
                  </div>
                  <BarChart values={bundle.walkForward.folds.map((f) => f.testReturnPct)} color="auto" height={90} />
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Fold</th><th>Train</th><th>Test</th><th>Params</th></tr></thead>
                      <tbody>
                        {bundle.walkForward.folds.map((f) => (
                          <tr key={f.fold}>
                            <td>{f.fold}</td>
                            <td className={`mono ${f.trainReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(f.trainReturnPct, 1)}%</td>
                            <td className={`mono ${f.testReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(f.testReturnPct, 1)}%</td>
                            <td className="mono faint">{Object.entries(f.bestParams).map(([k, v]) => `${k}=${v}`).join(' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Card>
          </div>

          <div className="grid cols-2">
            <Card title="Parameter sensitivity" subtitle={bundle.sensitivity.verdict}>
              <Heatmap
                cells={bundle.sensitivity.cells}
                xValues={bundle.sensitivity.xValues}
                yValues={bundle.sensitivity.yValues}
                xLabel={bundle.sensitivity.xKey}
                yLabel={bundle.sensitivity.yKey}
                format={(n) => (n < 1 && n > 0 ? n.toFixed(2) : n.toFixed(0))}
              />
              <div className="grid cols-3" style={{ marginTop: 8 }}>
                <Stat label="Fragility" value={fmtPctValue(bundle.sensitivity.fragilityScore * 100, 0)} tone={bundle.sensitivity.fragilityScore > 0.5 ? 'neg' : 'pos'} />
                <Stat label="Plateau" value={fmtPctValue(bundle.sensitivity.plateauFraction * 100, 0)} sub="configs near best" />
                <Stat label="Boundary peak" value={bundle.sensitivity.boundaryPeak ? 'Yes' : 'No'} tone={bundle.sensitivity.boundaryPeak ? 'neg' : 'pos'} />
              </div>
            </Card>

            <Card title="Monte Carlo resampling" subtitle="Block-bootstrap of returns — is the edge real or luck?">
              <Histogram
                values={bundle.bootstrap.distribution}
                markers={[
                  { value: 0, color: 'var(--text-faint)' },
                  { value: bundle.bootstrap.observedReturnPct, color: 'var(--accent)' },
                  { value: bundle.bootstrap.p5ReturnPct, color: 'var(--danger)' },
                  { value: bundle.bootstrap.p95ReturnPct, color: 'var(--info)' },
                ]}
              />
              <div className="legend" style={{ marginTop: 6 }}>
                <span><span className="swatch" style={{ background: 'var(--accent)' }} /> observed</span>
                <span><span className="swatch" style={{ background: 'var(--danger)' }} /> 5th pct</span>
                <span><span className="swatch" style={{ background: 'var(--info)' }} /> 95th pct</span>
              </div>
              <div className="grid cols-3" style={{ marginTop: 8 }}>
                <Stat label="P(profit)" value={fmtPctValue(bundle.bootstrap.pProfit * 100, 0)} tone={bundle.bootstrap.pProfit < 0.6 ? 'neg' : 'pos'} />
                <Stat label="5th pct return" value={fmtSigned(bundle.bootstrap.p5ReturnPct, 0) + '%'} tone={bundle.bootstrap.p5ReturnPct < 0 ? 'neg' : 'pos'} />
                <Stat label="Shuffle DD p95" value={fmtPctValue(bundle.permutation.p95MaxDdPct, 0)} sub={`obs ${bundle.permutation.observedMaxDdPct.toFixed(0)}%`} />
              </div>
            </Card>
          </div>

          <div className="grid cols-3">
            <Card title="Probability of backtest overfitting" subtitle="CSCV — chance the best config is a false discovery">
              <div className="stat-value" style={{ color: bundle.pbo.pbo > 0.5 ? 'var(--danger)' : bundle.pbo.pbo > 0.25 ? 'var(--warn)' : 'var(--accent)' }}>
                {bundle.pbo.insufficient ? '—' : fmtPctValue(bundle.pbo.pbo * 100, 0)}
              </div>
              <p className="muted" style={{ marginTop: 0 }}>{bundle.pbo.insufficient ? 'Not enough configs/segments to compute PBO.' : `${bundle.pbo.evaluations} train/test splits evaluated. Above 50% means the optimization is likely fitting noise.`}</p>
            </Card>

            <Card title="Cost sensitivity" subtitle="Does the edge survive realistic frictions?">
              <div className="kv"><span className="k">Base costs</span><span className={`v ${bundle.costSensitivity.baseReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(bundle.costSensitivity.baseReturnPct, 1)}%</span></div>
              <div className="kv"><span className="k">2× costs</span><span className={`v ${bundle.costSensitivity.doubleReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(bundle.costSensitivity.doubleReturnPct, 1)}%</span></div>
              <div className="kv"><span className="k">3× costs</span><span className={`v ${bundle.costSensitivity.tripleReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(bundle.costSensitivity.tripleReturnPct, 1)}%</span></div>
              <div className="kv"><span className="k">Edge survival</span><span className="v">{fmtPctValue(bundle.costSensitivity.survivalPct, 0)}</span></div>
              {!bundle.costSensitivity.survivesDoubleCosts && <Pill tone="red">Dies at 2× costs</Pill>}
            </Card>

            <Card title="Would this have worked before?" subtitle={bundle.eraReplay.verdict}>
              <BarChart values={bundle.eraReplay.eras.map((e) => e.returnPct)} color="auto" height={90} />
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Era</th><th>Return</th><th>Trades</th></tr></thead>
                  <tbody>
                    {bundle.eraReplay.eras.map((e) => (
                      <tr key={e.label}><td>{e.label}</td><td className={`mono ${e.returnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(e.returnPct, 1)}%</td><td className="mono">{e.trades}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bundle.eraReplay.regimeDependent && <Pill tone="red">Regime-dependent</Pill>}
            </Card>
          </div>

          <Card title={'\uD83D\uDD25 Red-team review'} subtitle="The case against this strategy — auto-generated because it needs to be heard">
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {bundle.redTeam.map((r, i) => <li key={i} style={{ marginBottom: 6 }}>{r}</li>)}
            </ul>
          </Card>

          <Card title="Verdict & filing">
            <div className="grid cols-2">
              <TextField label="Name this strategy" value={name} onChange={setName} placeholder={`${symbol} ${type}`} />
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <Button variant="primary" onClick={save}>Save as candidate</Button>
                <Button variant="danger" onClick={bury}>{'\u26B0'} Bury in graveyard</Button>
              </div>
            </div>
            <p className="help">Saving a candidate adds it to the comparison table. Burying records an autopsy so you (and future-you) remember why it failed.</p>
          </Card>
        </>
      )}
    </div>
  )
}
